// ABOUTME: Screen 2A Pick the Feeling: a carousel of feelings, each a full scene over the shared sea; confirm_feeling sends the focused one.
// ABOUTME: The carousel opens on the stored feeling, and browsing without confirming counts as unsent.

import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useCrossingContent } from '../../../hooks/useCrossingContent';
import { CROSSING_SUB_INK } from '../../../theme/crossingTokens';
import { useCrossingReducedMotion } from '../crossing/useReducedMotion';
import ReadingCarousel from '../crossing/ReadingCarousel';
import type { ScreenProps, ScreenResult } from '../runner/screenContract';
import PickStatus from './carousel/PickStatus';
import { useCarouselIndex } from './carousel/useCarouselIndex';
import { useCarouselKeys } from './carousel/useCarouselKeys';
import FeelingScene from './feelings/FeelingScene';

export default function Screen2A({ crossing }: ScreenProps): ScreenResult {
  const { data: content } = useCrossingContent();
  const reducedMotion = useCrossingReducedMotion();
  const feelings = useMemo(() => Object.values(content?.feelings ?? {}), [content]);
  const { focused, index, count, prev, next, browsed } = useCarouselIndex(feelings, crossing.feeling);
  useCarouselKeys(prev, next);

  // The text and arrows sit up top; the focused feeling's scene is drawn behind them, standing on the horizon.
  const layer = (
    <Box
      sx={{
        position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'flex-start', gap: 2, p: 3, textAlign: 'center',
      }}
    >
      <Typography sx={{ fontSize: '11px', fontWeight: 900, letterSpacing: '3px', color: CROSSING_SUB_INK }}>
        WHAT FEELING SHOWED UP?
      </Typography>
      {count > 0 && (
        <ReadingCarousel
          items={feelings}
          activeIndex={index}
          reducedMotion={reducedMotion}
          renderItem={feeling => <FeelingScene feeling={feeling} />}
        />
      )}
      {focused && (
        <Typography key={focused.code} variant="body2" data-testid="feeling-question">{focused.question}</Typography>
      )}
      <PickStatus name={focused?.name} index={index} count={count} />
    </Box>
  );

  return {
    layer,
    headline: 'Use the arrows to see which feeling showed up first.',
    ...(focused && { picker: { label: focused.name, prevLabel: 'Previous feeling', nextLabel: 'Next feeling', onPrev: prev, onNext: next } }),
    slots: {
      confirm_feeling: { disabled: !focused, values: focused ? { feeling: focused.code } : undefined },
    },
    hasUnsentState: browsed && !!focused && focused.code !== crossing.feeling,
  };
}
